import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, RefreshCw } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { fmtDateTime } from "../../lib/helpers";
import { Section, Field, Empty } from "../common";

const ST = { completed: ["Answered", "Contestada", "text-emerald-700"], "in-progress": ["Answered", "Contestada", "text-emerald-700"], calling: ["Calling…", "Llamando…", "text-sky-700"], "no-answer": ["No answer", "Sin respuesta", "text-amber-700"], busy: ["Busy", "Ocupado", "text-amber-700"], failed: ["Failed", "Falló", "text-red-700"], canceled: ["Canceled", "Cancelada", "text-red-700"] };

function CallLogs({ tick }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/call-alerts/logs").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load, tick]);
  return (
    <Section title={t("Recent alert calls", "Llamadas de alerta recientes")} testid="call-logs" count={items?.length}
      action={<button className="btn-ghost btn-sm" onClick={load} data-testid="call-logs-refresh"><RefreshCw className="w-4 h-4" /></button>}>
      {items && !items.length && <Empty text={t("No calls yet", "Aún no hay llamadas")} testid="call-logs-empty" />}
      {items?.map((x) => {
        const [en, es, cls] = ST[x.status] || [x.status, x.status, "text-slate-600"];
        return (
          <div key={x.id} className="px-4 py-2 border-b flex items-center gap-3 text-sm" data-testid={`call-log-${x.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{x.kind === "test" ? t("Test call", "Llamada de prueba") : x.lead_name} → {x.to}</div>
              <div className="text-xs text-slate-500 truncate">{fmtDateTime(x.at, lang)}{x.attempt > 1 && ` · ${t("2nd try", "2.º intento")}`}{x.error && ` · ${x.error}`}</div>
            </div>
            <span className={`text-xs font-semibold shrink-0 ${cls}`} data-testid={`call-log-status-${x.id}`}>{lang === "es" ? es : en}</span>
          </div>
        );
      })}
    </Section>
  );
}

export function CallAlerts() {
  const { t } = useLang();
  const [c, setC] = useState(null);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/call-alerts").then((r) => setC({ ...r.data, to_text: r.data.to_numbers.join(", ") })).catch(() => {}); }, []);
  if (!c) return null;
  const f = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/call-alerts", { ...c, api_key_secret: secret, to_numbers: c.to_text.split(/[,;\n]/) });
      setC({ ...r.data, to_text: r.data.to_numbers.join(", ") }); setSecret(""); toast.success(t("Call alerts saved", "Alertas por llamada guardadas"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/call-alerts/test");
      r.data.forEach((x) => (x.ok ? toast.success(`${t("Calling", "Llamando a")} ${x.to}`) : toast.error(`${x.to}: ${x.error}`)));
      setTick((n) => n + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const inp = (k, label, ph, type = "text") => <Field label={label}><input className="inp" type={type} value={c[k] || ""} onChange={f(k)} placeholder={ph} data-testid={`call-alerts-${k}`} /></Field>;
  return (
    <div className="grid gap-3" data-testid="call-alerts">
      <Section title={t("Call alert for new Google Ads leads (Twilio)", "Alerta por llamada para leads nuevos de Google Ads (Twilio)")} testid="call-alerts-settings"
        action={<Switch checked={!!c.enabled} onCheckedChange={(v) => setC({ ...c, enabled: v })} data-testid="call-alerts-enabled" />}>
        <div className="p-4 grid gap-3">
          <p className="text-sm text-slate-500">{t("When a REAL Google Ads lead arrives, these phones get an automatic call with the customer's name and phone. Test leads never call. If not answered, it calls again after 2 minutes. Twilio bills these calls to your Twilio account.", "Cuando llega un lead REAL de Google Ads, estos teléfonos reciben una llamada automática con el nombre y teléfono del cliente. Los leads de prueba nunca llaman. Si no contesta, vuelve a llamar a los 2 minutos. Twilio cobra estas llamadas a su cuenta de Twilio.")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {inp("account_sid", "Account SID", "AC…")}
            {inp("api_key_sid", "API Key SID", "SK…")}
            <Field label={`API Key Secret${c.has_secret ? t(" (saved – leave blank to keep)", " (guardado – deje vacío para mantener)") : ""}`}><input className="inp" type="password" autoComplete="new-password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={c.has_secret ? "••••••••" : ""} data-testid="call-alerts-api_key_secret" /></Field>
            {inp("from_number", t("Twilio phone number (caller)", "Número de Twilio (el que llama)"), "+1 813 555 0100", "tel")}
            {inp("to_text", t("Phones to call (comma separated)", "Teléfonos a llamar (separados por coma)"), "813 555 0101, 305 555 0102", "tel")}
            {inp("spoken_name", t("Name spoken in the call", "Nombre que dice la llamada"), "H Calix")}
          </div>
          <label className="flex items-center justify-between text-sm font-semibold"><span>{t("Call again after 2 minutes if not answered", "Volver a llamar a los 2 minutos si no contesta")}</span><Switch checked={!!c.retry} onCheckedChange={(v) => setC({ ...c, retry: v })} data-testid="call-alerts-retry" /></label>
          <label className="flex items-center justify-between text-sm font-semibold"><span>{t("Also call me for new website leads", "También llamarme por leads nuevos del sitio web")}</span><Switch checked={c.call_website !== false} onCheckedChange={(v) => setC({ ...c, call_website: v })} data-testid="call-alerts-website" /></label>
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" disabled={busy} onClick={save} data-testid="call-alerts-save">{t("Save", "Guardar")}</button>
            <button className="btn-outline" disabled={busy || !c.ready} onClick={test} data-testid="call-alerts-test"><PhoneCall className="w-4 h-4" /> {t("Call me now (test)", "Llamarme de prueba")}</button>
          </div>
        </div>
      </Section>
      <CallLogs tick={tick} />
    </div>
  );
}
